import { useState, useEffect } from 'react';
import { getToken } from '../../services/api';
import { localDateOnly } from '../../utils/localDate';

// Get API base URL using the same logic as api.js
const getApiBaseUrl = () => {
  if (import.meta.env.PROD) {
    // For iOS devices, sometimes relative URLs fail - try absolute URL fallback
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS && window.location.origin) {
      return window.location.origin + '/api';
    }
    // In production, use relative URL for same-origin requests
    return '/api';
  } else {
    // In development, use the configured URL
    return import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
  }
};

const RawChapterReport = ({ readers, onSuccess }) => {
  const [selectedReader, setSelectedReader] = useState(null);
  const [chapterCount, setChapterCount] = useState('');
  const [reportedDate, setReportedDate] = useState(localDateOnly());
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Set default to primary reader
  useEffect(() => {
    if (readers.length > 0 && !selectedReader) {
      const primaryReader = readers.find(r => r.isPrimary) || readers[0];
      setSelectedReader(primaryReader);
    }
  }, [readers, selectedReader]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('🔄 Form submit handler called');
    
    if (!selectedReader) {
      console.error('❌ No reader selected');
      setError('Please select a reader.');
      return;
    }

    if (!chapterCount || parseInt(chapterCount) < 1) {
      console.error('❌ Invalid chapter count:', chapterCount);
      setError('Please enter a valid number of chapters (1 or more).');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccessMessage('');

      const apiBaseUrl = getApiBaseUrl();
      const apiUrl = `${apiBaseUrl}/raw-chapter-reports`;
      const payload = {
        readerId: selectedReader.id,
        chapterCount: parseInt(chapterCount),
        reportedDate,
        notes: notes.trim() || undefined
      };

      const token = getToken();
      
      console.log('🚀 Submitting raw chapter report:', {
        apiUrl,
        payload,
        token: token ? 'present' : 'missing'
      });

      if (!token) {
        throw new Error('No authentication token found. Please sign in again.');
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      console.log('📡 Response status:', response.status, response.statusText);

      const data = await response.json();
      
      console.log('📥 Response data:', data);

      if (!response.ok) {
        throw new Error(data.error || 'Failed to log chapters');
      }

      setSuccessMessage(`Successfully logged ${chapterCount} chapter(s) for ${selectedReader.name}!`);
      
      // Reset form
      setChapterCount('');
      setNotes('');
      
      // Notify parent of success
      if (onSuccess) {
        onSuccess(data.report);
      }

    } catch (err) {
      console.error('❌ Raw chapter report error:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      });
      setError(err.message || 'Failed to log chapters');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="raw-chapter-report-section">
      <p className="raw-chapter-description">
        Log a total number of chapters without saying which ones, like “I read 50 chapters”.
      </p>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          <p>{successMessage}</p>
        </div>
      )}

      <form 
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(e);
        }} 
        className="raw-chapter-form"
      >
        {/* Reader Selection */}
        {readers.length > 1 && (
          <div className="form-group">
            <label htmlFor="raw-reader-select">Reader:</label>
            <select
              id="raw-reader-select"
              value={selectedReader?.id || ''}
              onChange={(e) => {
                const reader = readers.find(r => r.id === e.target.value);
                setSelectedReader(reader);
              }}
              disabled={submitting}
            >
              {readers.map(reader => (
                <option key={reader.id} value={reader.id}>
                  {reader.name} {reader.isPrimary ? '(You)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Chapter Count */}
        <div className="form-group">
          <label htmlFor="chapter-count">Number of Chapters:</label>
          <input
            type="number"
            id="chapter-count"
            min="1"
            value={chapterCount}
            onChange={(e) => setChapterCount(e.target.value)}
            placeholder="e.g., 50"
            disabled={submitting}
            required
          />
        </div>

        {/* Date */}
        <div className="form-group">
          <label htmlFor="raw-read-date">Date</label>
          <input
            type="date"
            id="raw-read-date"
            value={reportedDate}
            onChange={(e) => setReportedDate(e.target.value)}
            disabled={submitting}
          />
        </div>

        {/* Notes (optional) */}
        <div className="form-group">
          <label htmlFor="raw-notes">Notes (optional):</label>
          <textarea
            id="raw-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g., Genesis 1-50, listened to audio bible..."
            rows={2}
            disabled={submitting}
          />
        </div>

        {/* Submit Button */}
        <button 
          type="button"
          onClick={handleSubmit}
          className="submit-button raw-submit"
          disabled={submitting}
        >
          {submitting ? 'Logging...' : 'Log chapters'}
        </button>
      </form>
    </div>
  );
};

export default RawChapterReport;

